import { t } from "ttag";

import type { VisualizationSettingsDefinitions } from "metabase/visualizations/types";

import { getDefaultTextColor } from "./text-card-styles";

/**
 * The formatting settings shared by dashboard `text` and `heading` cards.
 *
 * Both card types get an identical set, defined once, so the two cards can't drift apart as
 * settings are added. Keys are namespaced `text.*` to match the existing
 * `text.align_horizontal` / `text.align_vertical`, and every value is a plain string — never
 * an array or nested map, which keeps them safe through `normalize-visualization-settings`
 * and serdes.
 *
 * Note there is no "reset to default" in this modal — it is disabled for virtual cards
 * because resetting one wipes the card's text (metabase#14644) — so every setting has an
 * explicit default option (`medium`, `default`, `normal`) that the user can return to by
 * hand.
 */

type TextCardSettingsOptions = {
  /** Text cards sit at the top by default; headings are vertically centred. */
  defaultAlignVertical?: "top" | "middle" | "bottom";
};

export function getTextCardSettings({
  defaultAlignVertical = "top",
}: TextCardSettingsOptions = {}): VisualizationSettingsDefinitions {
  return {
    "text.align_vertical": {
      getSection: () => t`Display`,
      title: t`Vertical Alignment`,
      widget: "select",
      getProps: () => ({
        options: [
          { name: t`Top`, value: "top" },
          { name: t`Middle`, value: "middle" },
          { name: t`Bottom`, value: "bottom" },
        ],
      }),
      getDefault: () => defaultAlignVertical,
    },
    "text.align_horizontal": {
      getSection: () => t`Display`,
      title: t`Horizontal Alignment`,
      widget: "select",
      getProps: () => ({
        options: [
          { name: t`Left`, value: "left" },
          { name: t`Center`, value: "center" },
          { name: t`Right`, value: "right" },
        ],
      }),
      getDefault: () => "left",
    },
    "text.font_size": {
      getSection: () => t`Typography`,
      group: t`Font`,
      title: t`Font size`,
      widget: "segmentedControl",
      getProps: () => ({
        options: [
          { name: t`Small`, value: "small" },
          { name: t`Medium`, value: "medium" },
          { name: t`Large`, value: "large" },
          { name: t`X-Large`, value: "x_large" },
        ],
      }),
      getDefault: () => "medium",
    },
    "text.font_weight": {
      getSection: () => t`Typography`,
      group: t`Font`,
      title: t`Font weight`,
      widget: "select",
      getProps: () => ({
        options: [
          { name: t`Default`, value: "default" },
          { name: t`Light`, value: "light" },
          { name: t`Regular`, value: "regular" },
          { name: t`Medium`, value: "medium" },
          { name: t`Semibold`, value: "semibold" },
          { name: t`Bold`, value: "bold" },
        ],
      }),
      getDefault: () => "default",
    },
    "text.font_style": {
      getSection: () => t`Typography`,
      group: t`Font`,
      title: t`Font style`,
      widget: "segmentedControl",
      getProps: () => ({
        options: [
          { name: t`Normal`, value: "normal" },
          { name: t`Italic`, value: "italic" },
        ],
      }),
      getDefault: () => "normal",
    },
    "text.text_color": {
      getSection: () => t`Typography`,
      group: t`Font`,
      widget: "color",
      getProps: () => ({ title: t`Text color` }),
      getDefault: () => getDefaultTextColor(),
    },
  };
}
